import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { darken } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddCardIcon from '@mui/icons-material/AddCard';
import MessageIcon from '@mui/icons-material/Message';

import useParam from '../../../context/ParamContext';
import JmEasyJobmonitorCard from './JmEasyJobmonitorCard';
import masterDataService from '../../../service/masterData.service';
import { generateAutocompleteItems, getContrastColor } from '../../../utils/helper';
import { AddButtonUi, AutoCompleteSelectUI, DebouncedInputUI, FormLabelUI, SwitchUI } from '../../Interface';
import userService from '../../../service/user.service';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import productionService from '../../../service/production.service';
import { Tooltip, Typography, Box, InputAdornment, IconButton, Popover, Badge, Stack } from '@mui/material';
import sourcelistService from '../../../service/sourcelist.service';
import { useLoader } from '../../../provider/LoaderProvider';
import jobmonitorEasyService from '../../../service/jobMonitorEasy.service';
import useUsers from '../../../context/UsersContext';
import ProdNonProductiveWorkDialog from '../../ProdNonProductiveWork/ProdNonProductiveWorkDialog';
import ProductionDialog from './ProductionDialog';
import JmEasyAttachmentDialog from './JmEasyAttachmentDialog';
import CommentDialog from '../../Comment/CommentDialog';
import commentService from '../../../service/comment.service';
import colorFinder from '../../../utils/colorFinder';
import PinCodeLogInDialog from '../../Interface/PinCodeLogInDialog';
import ConfirmDialogV2UI from '../../Interface/ConfirmDialogV2UI';

export const JmEasyJobmonitorPage = () => {
  const { getUser, user } = useUsers();
  const { showLoader, hideLoader } = useLoader();
  const timeoutRef = useRef(null);
  const { getParam, params } = useParam();
  const [prodOperations, setProdOperations] = useState({ rows: [], rowCount: 0 });
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    groupReporting: [],
    status: [],
    scrap: [],
    project: [],
  });
  const [reasons, setReasons] = useState({
    interruption: [],
    downtime: [],
    interimReport: null,
  });
  const [helperData, setHelperData] = useState({});
  const [refetch, setRefetch] = useState(false);
  const [filter, setFilter] = useState({
    userId: null,
    pinCode: '',
    projectId: null,
  });
  const [query, setQuery] = useState({
    filter: {
      filterModel: {
        items: [],
        logicOperator: 'and',
      },
    },
    sorting: {
      sortModel: [],
    },
    pagination: {
      paginationModel: {},
    },
  });
  const [showDialog, setShowDialog] = useState(false);
  const [disableBtn, setDisableBtn] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [workReportDialogData, setWorkReportDialogData] = useState({ open: false, data: {} });
  const [productionDialog, setProductionDialog] = useState({ open: false, data: {} });
  const [attachmentDialog, setAttachmentDialog] = useState({ open: false, attachments: [], productionId: null });
  const [projectCommentCount, setProjectCommentCount] = useState(0);
  const [projects, setProjects] = useState([]);
  const [interruptedProductions, setInterruptedProductions] = useState([]);

  const defCommentDialogData = {
    open: false,
    lineId: null,
    subMenuUrl: '',
  };
  const [commentDialogData, setCommentDialogData] = useState(defCommentDialogData);

  const defaultConfirmDialogData = {
    open: false,
    message: '',
    buttons: [
      {
        label: 'Megerősít',
        variant: 'outlined',
        color: 'inherit',
        onClick: () => {},
      },
    ],
  };
  const [confirmDialogData, setConfirmDialogData] = useState(defaultConfirmDialogData);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getParam();
    initDefaultValues();
  }, []);

  useEffect(() => {
    if (filter.pinCode) {
      getUserByPinCode();
    } else {
      setProjects([]);
      setShowDialog(true);
    }
  }, [filter.pinCode]);

  useEffect(() => {
    getUserActiveProductions();
  }, [filter.userId]);

  useEffect(() => {
    if (Object.keys(params).length > 0 && filter.projectId) {
      const statusConfig = JSON.parse(params.JOBMONITOR_STATUSES);
      const statusIds = statusConfig.noPreviousWork;

      handleFilterChange(
        [
          { field: 'projectId', operator: 'is', value: filter.projectId },
          { field: 'prodOpStatusId', operator: 'isAnyOf', value: statusIds },
        ],
        null,
        null,
        true
      );
    }
  }, [params, filter.projectId]);

  useEffect(() => {
    if (query.filter.filterModel.items.length > 0) {
      getList();
    }
  }, [query]);

  useEffect(() => {
    if (refetch && query.filter.filterModel.items.length > 0) {
      getList();
      getProjectCommentCount();
      setRefetch(false);
    }
  }, [refetch]);

  useEffect(() => {
    getProjectCommentCount();
  }, [filter.projectId]);

  useEffect(() => {
    if (filter.operationId && filter.projectId) {
      getGroupReportingOptions();
    }
  }, [filter.operationId, filter.projectId]);

  useEffect(() => {
    if (params.USE_COMPANY_BREAK === 'Y' && filter.projectId) {
      const intervalId = setInterval(() => {
        getBreakByUserId();
      }, 60000);

      return () => clearInterval(intervalId);
    }
  }, [filter.projectId]);

  useEffect(() => {
    if ((!filter.projectId && !params.VM_OPERATION_WIDGET_ACTIVITY) || !Number(params.VM_OPERATION_WIDGET_ACTIVITY)) {
      return;
    }

    const handleActivity = () => resetTimer();

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    resetTimer();

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      clearTimeout(timeoutRef.current);
    };
  }, [filter.projectId, params]);

  const getUserByPinCode = () => {
    setProdOperations({ rows: [], rowCount: 0 });
    userService.getUserByPinCode(filter.pinCode).then((data) => {
      if (Object.keys(data).length > 0) {
        setFilter((prev) => ({
          ...prev,
          nickName: data.nickName,
          userId: data.userId,
        }));
        getProjects(data.userId, data.employeeId);
        setShowDialog(false);
      } else {
        setFilter((prev) => ({
          ...prev,
          pinCode: '',
        }));
        addNotification({
          content: 'Nem található felhasználó ezzel a PIN kóddal!',
          type: NotificationType.ERROR,
        });
      }
    });
  };

  const getUserActiveProductions = () => {
    if (!filter.userId) {
      setInterruptedProductions([]);
      return;
    }

    jobmonitorEasyService.getUserActiveProductions(filter.userId).then((data) => {
      setInterruptedProductions(data);
    });
  };

  const getProjects = (userId, employeeId) => {
    const canViewAllProjects = JSON.parse(params.JM_EASY_CAN_VIEW_ALL_PROJECTS || '[]').includes(userId);

    masterDataService.projectToForm().then((data) => {
      const filteredProjects = data.filter((project) => {
        if (!project.active) return false;

        if (canViewAllProjects) return true;

        if (!employeeId) return false;

        return project.projectEmployee?.some((pe) => pe.employeeId === employeeId);
      });

      setProjects(filteredProjects);
    });
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .productionStatusToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .interruptionReasonsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .downTimeReasonsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .operationGroupToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .scrapReasonsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .itemsFromTableToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );
    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .warehouseToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          status: resp[0],
          interruption: resp[1],
          downtime: resp[2],
          operationGroup: resp[3],
          scrap: resp[4],
          items: resp[5],
          warehouse: resp[6],
        };

        const filteredInterruptions = aData.interruption.filter((i) => i.active);
        const normalInterruptions = filteredInterruptions.filter((i) => !i.interimReport);
        const interimReport = filteredInterruptions.find((i) => i.interimReport) ?? null;

        setReasons({ downtime: aData.downtime, interruption: normalInterruptions, interimReport });
        setHelperData(aData);

        const acOptions = {
          operations: generateAutocompleteItems(aData.operations, 'operationName', 'operationId'),
          status: generateAutocompleteItems(aData.status, 'name', 'code'),
          scrap: generateAutocompleteItems(aData.scrap, 'name', 'id'),
        };

        setAutoCompleteOptions((prev) => ({
          ...prev,
          ...acOptions,
        }));
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const resetTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setFilter((prev) => ({
        ...prev,
        projectId: null,
        pinCode: '',
        userId: null,
        nickName: '',
      }));

      resetTimer();
    }, params.VM_OPERATION_WIDGET_ACTIVITY);
  };

  const getGroupReportingOptions = () => {
    masterDataService.groupReportingToForm(filter.operationId, filter.projectId).then((data) => {
      if (data.length > 0) {
        const transformed = generateAutocompleteItems(data, ['reference', 'finishedItemCode', 'name'], 'id');

        setAutoCompleteOptions((prev) => ({
          ...prev,
          groupReporting: transformed,
        }));
      }
    });
  };

  const getList = () => {
    showLoader();
    jobmonitorEasyService
      .getProductionOperationsFromView(query)
      .then((data) => {
        setProdOperations(data);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleFilterChange = (fieldOrFilters, operator = null, value = null, clearExisting = false) => {
    setQuery((prev) => {
      const filtersToAdd = Array.isArray(fieldOrFilters)
        ? fieldOrFilters
        : [{ field: fieldOrFilters, operator, value }];

      const validFilters = filtersToAdd
        .filter((f) => f.value !== null && f.value !== undefined)
        .map((f) => ({
          field: f.field,
          operator: f.operator ?? operator,
          value: f.value?.value ?? f.value,
        }));

      return {
        ...prev,
        filter: {
          filterModel: {
            items: clearExisting
              ? validFilters
              : [
                  ...prev.filter.filterModel.items.filter((item) => !filtersToAdd.some((f) => f.field === item.field)),
                  ...validFilters,
                ],
          },
        },
      };
    });
  };

  const getBreakByUserId = () => {
    productionService.getBreakByUserId(filter.projectId).then((breakData) => {
      setProdOperations((prev) => {
        const updatedRows = prev.rows.map((row) => {
          const updated = breakData.find((item) => item.id === row.id);
          if (updated) {
            return {
              ...row,
              productionReport: updated.productionReport,
            };
          }
          return row;
        });

        return {
          ...prev,
          rows: updatedRows,
        };
      });
    });
  };

  const getAttachments = (productionId) => {
    setAttachmentDialog((prev) => ({
      ...prev,
      open: true,
    }));

    productionService.getAllAttachmentsByProductionId(productionId).then((data) => {
      setAttachmentDialog((prev) => ({
        ...prev,
        attachments: data,
        productionId,
      }));
    });
  };

  const handleProjectSelect = (projectId) => {
    setProdOperations({ rows: [], rowCount: 0 });
    setFilter((prev) => ({
      ...prev,
      projectId: projectId ?? null,
    }));
    setShowDialog(false);
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const getProjectCommentCount = () => {
    if (!filter.projectId) {
      setProjectCommentCount(0);
      return;
    }

    commentService.getItemCount('Project', filter.projectId).then((data) => {
      setProjectCommentCount(data.count);
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialogData({ ...defaultConfirmDialogData });
  };

  const formatActiveProductionText = (row) => {
    const { productionStatusId, operation, production } = row;

    const statusText = productionStatusId === 'INPROGRESS' ? 'Folyamatban lévő' : 'Megszakított';

    return `${statusText} "${operation.operationName}" feladat a(z) ${production.project.projectName} nevű projektből.`;
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        height: 'calc(100vh - 48px)',
      }}
    >
      {projects.length > 0 && (
        <Box
          sx={{
            px: 2,
            overflowX: 'auto',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: 'fit-content',
              mx: 'auto',
              alignItems: 'stretch',
              py: 1,
            }}
          >
            {projects.map((project) => {
              const isSelected = String(project.projectId) === String(filter.projectId);
              const projectName = project.projectName ?? '';
              const shortName = projectName.substring(0, 15);

              return (
                <Tooltip key={project.projectId} title={projectName} arrow>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => handleProjectSelect(project.projectId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleProjectSelect(project.projectId);
                      }
                    }}
                    sx={{
                      minWidth: 90,
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      borderStyle: 'solid',
                      bgcolor: isSelected ? '#56A5F7' : 'color-mix(in srgb, #2196f3, #fff 90%)',
                      color: isSelected ? 'white' : '#1E88E5',
                      cursor: 'pointer',
                      userSelect: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 20 }} variant="body2">
                      {shortName}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      <Grid
        container
        spacing={{
          xs: 1,
          sm: 2,
        }}
        alignItems="center"
        sx={{
          px: 2,
          mb: 2,
          justifyContent: {
            xs: 'center',
            sm: 'flex-start',
          },
        }}
      >
        <Grid
          item
          xs="auto"
          sx={{
            display: {
              xs: 'none',
              md: 'block',
            },
          }}
        >
          <Box pt="0.5rem">
            <AddButtonUi
              sx={{
                fontSize: 20,
                backgroundColor: '#52D55F',
              }}
              text="Gyártás"
              onClick={() =>
                setProductionDialog({
                  open: true,
                  data: { projectId: filter.projectId },
                })
              }
              disabled={!filter.projectId}
            />
          </Box>
        </Grid>
        <Grid item xs="auto">
          <Box pt="0.5rem">
            <Badge
              showZero
              badgeContent={projectCommentCount}
              color="secondary"
              sx={{
                color: 'grey',
                mt: 1.5,
                cursor: filter.projectId ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (!filter.projectId) return;

                setCommentDialogData({
                  open: true,
                  lineId: filter.projectId,
                  subMenuUrl: 'Project',
                });
              }}
            >
              <MessageIcon />
            </Badge>
          </Box>
        </Grid>
        {filter?.nickName && (
          <Grid
            item
            xs={12}
            sm="auto"
            sx={{
              ml: {
                xs: 0,
                sm: 'auto',
              },
              justifyContent: {
                xs: 'center',
                sm: 'flex-end',
              },
            }}
          >
            <Box
              pt={{
                xs: 0,
                sm: '1rem',
              }}
              display="flex"
              gap="0.5rem"
              alignItems="center"
              justifyContent="center"
            >
              <AccountCircleIcon
                sx={{
                  fontSize: '28px',
                  color: '#4481D3',
                }}
              />

              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: '#4481D3',
                  fontSize: '20px',
                }}
              >
                {filter.nickName}
              </Typography>
              <Typography
                sx={{
                  ml: '1rem',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  color: '#4481D3',
                  fontSize: '20px',
                }}
                onClick={() =>
                  setFilter((prev) => ({
                    ...prev,
                    projectId: null,
                    pinCode: '',
                    nickName: '',
                    userId: null,
                  }))
                }
              >
                Kilépés
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
      <Box sx={{ px: 2, pb: 0.5 }}>
        {filter.projectId ? (
          prodOperations.rows.map((operation) => (
            <JmEasyJobmonitorCard
              key={operation.productionoperationid}
              data={operation}
              setRefetch={setRefetch}
              userId={filter.userId}
              rows={prodOperations.rows}
              params={params}
              helperData={helperData}
              reasons={reasons}
              autoCompleteOptions={autoCompleteOptions}
              getAttachments={getAttachments}
              setConfirmDialogData={setConfirmDialogData}
              hideConfirmDialog={hideConfirmDialog}
            />
          ))
        ) : (
          <Stack direction="row" gap={1} justifyContent="center" flexWrap="wrap">
            {interruptedProductions.map((row) => (
              <Box
                key={row.id}
                width={{
                  xs: '100%',
                  md: 400,
                }}
                backgroundColor={colorFinder(row.productionStatus.color)?.jobMonitorChipColor}
                p={2}
                borderRadius={2}
                onClick={() => handleProjectSelect(row.production.projectId)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    background: colorFinder(row.productionStatus.color)?.jobMonitorHoverColor,
                  },
                }}
              >
                <Typography fontWeight="bold">{formatActiveProductionText(row)}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <PinCodeLogInDialog
        open={showDialog}
        handleClose={() => setShowDialog(false)}
        setLogInData={setFilter}
        logInData={filter}
      />

      <ProdNonProductiveWorkDialog
        dialogData={workReportDialogData}
        handleClose={() => setWorkReportDialogData({ open: false, data: {} })}
      />

      <ProductionDialog
        dialogData={productionDialog}
        changed={{ setRefetch }}
        handleClose={() => setProductionDialog({ open: false, data: {} })}
      />

      <JmEasyAttachmentDialog
        dialogData={attachmentDialog}
        handleClose={() => setAttachmentDialog({ open: false, attachments: [], productionId: null })}
        userId={filter.userId}
        params={params}
      />

      <CommentDialog
        dialogData={commentDialogData}
        handleClose={() => setCommentDialogData(defCommentDialogData)}
        setRefetchTable={setRefetch}
      />

      <ConfirmDialogV2UI
        {...confirmDialogData}
        largeButtons={true}
        maxWidth="lg"
        fullWidth={true}
        onClose={hideConfirmDialog}
      />
    </Box>
  );
};
